import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Mail, Lock, Shield, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Une erreur est survenue. Veuillez réessayer.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

const LoginPage = () => {
  const { login, verify2FA, pending2FA } = useAuth();
  const [mode, setMode] = useState('login'); // login, 2fa, forgot, reset
  const [loading, setLoading] = useState(false);
  const [resetToken, setResetToken] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    code: '',
    newPassword: ''
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await login(formData.email, formData.password);
      if (result.requires_2fa) {
        setMode('2fa');
        toast.info("Entrez votre code d'authentification");
      } else {
        toast.success("Connexion réussie");
      }
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await verify2FA(formData.code);
      toast.success("Authentification reussie");
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!formData.email) {
      toast.error("Veuillez entrer votre email");
      return;
    }
    setLoading(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL + '/api';
      const res = await fetch(`${API}/auth/request-password-reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email })
      });
      const data = await res.json();
      if (data.reset_token) {
        setResetToken(data.reset_token);
        setMode('reset');
        toast.success("Token de reinitialisation genere");
      } else {
        toast.info(data.message || "Si un compte existe, un lien a ete envoye.");
        setMode('login');
      }
    } catch (error) {
      toast.error("Erreur lors de la demande");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (formData.newPassword.length < 6) {
      toast.error("Le mot de passe doit contenir au moins 6 caracteres");
      return;
    }
    setLoading(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL + '/api';
      const res = await fetch(`${API}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: resetToken, new_password: formData.newPassword })
      });
      const data = await res.json();
      if (res.ok) {
        toast.success("Mot de passe reinitialise avec succes");
        setMode('login');
        setResetToken('');
        setFormData(prev => ({ ...prev, newPassword: '' }));
      } else {
        toast.error(data.detail || "Erreur lors de la reinitialisation");
      }
    } catch (error) {
      toast.error("Erreur lors de la reinitialisation");
    } finally {
      setLoading(false);
    }
  };

  // 2FA verification screen
  if (mode === '2fa' || pending2FA) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="w-full max-w-sm">
          <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6">
            <div className="text-center mb-6">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Shield className="w-6 h-6 text-blue-600" />
              </div>
              <h1 className="text-lg font-semibold text-slate-900">Double authentification</h1>
              <p className="text-sm text-slate-500 mt-1">
                Entrez le code de votre application d'authentification
              </p>
            </div>

            <form onSubmit={handleVerify2FA} className="space-y-4">
              <div>
                <Label className="text-sm text-slate-600">Code a 6 chiffres</Label>
                <Input
                  type="text"
                  value={formData.code}
                  onChange={(e) => handleChange('code', e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="h-12 text-center text-2xl tracking-widest font-mono"
                  placeholder="000000"
                  maxLength={6}
                  autoFocus
                  data-testid="2fa-code-input"
                />
              </div>

              <Button
                type="submit"
                disabled={loading || formData.code.length !== 6}
                className="w-full h-10 bg-slate-900 hover:bg-slate-800"
                data-testid="verify-2fa-btn"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    Verifier
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>

              <button
                type="button"
                onClick={() => { setMode('login'); setFormData(prev => ({ ...prev, code: '' })); }}
                className="w-full text-sm text-slate-500 hover:text-slate-700"
              >
                Retour a la connexion
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Forgot password screen
  if (mode === 'forgot') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="w-full max-w-sm">
          <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6">
            <div className="text-center mb-6">
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Lock className="w-6 h-6 text-amber-600" />
              </div>
              <h1 className="text-lg font-semibold text-slate-900">Mot de passe oublie</h1>
              <p className="text-sm text-slate-500 mt-1">
                Entrez votre email pour reinitialiser votre mot de passe
              </p>
            </div>

            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div>
                <Label className="text-sm text-slate-600">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    className="h-10 pl-10"
                    placeholder="vous@exemple.com"
                    required
                    autoFocus
                    data-testid="forgot-email-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-10 bg-slate-900 hover:bg-slate-800"
                data-testid="forgot-submit-btn"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    Reinitialiser
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>

              <button
                type="button"
                onClick={() => setMode('login')}
                className="w-full text-sm text-slate-500 hover:text-slate-700"
                data-testid="back-to-login-btn"
              >
                Retour a la connexion
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Reset password screen (after token received)
  if (mode === 'reset') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="w-full max-w-sm">
          <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6">
            <div className="text-center mb-6">
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Shield className="w-6 h-6 text-emerald-600" />
              </div>
              <h1 className="text-lg font-semibold text-slate-900">Nouveau mot de passe</h1>
              <p className="text-sm text-slate-500 mt-1">
                Choisissez votre nouveau mot de passe
              </p>
            </div>

            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <Label className="text-sm text-slate-600">Nouveau mot de passe</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type="password"
                    value={formData.newPassword}
                    onChange={(e) => handleChange('newPassword', e.target.value)}
                    className="h-10 pl-10"
                    placeholder="Min. 6 caracteres"
                    minLength={6}
                    required
                    autoFocus
                    data-testid="reset-password-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-10 bg-emerald-700 hover:bg-emerald-800"
                data-testid="reset-submit-btn"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    Confirmer
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>

              <button
                type="button"
                onClick={() => { setMode('login'); setResetToken(''); }}
                className="w-full text-sm text-slate-500 hover:text-slate-700"
              >
                Retour a la connexion
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6">
          {/* Header */}
          <div className="text-center mb-6">
            <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'var(--font-heading)' }}>
              Commercial Mail Sync
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Connectez-vous à votre compte
            </p>
          </div>

          {/* Login Form */}
          {mode === 'login' && (
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label className="text-sm text-slate-600">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    className="h-10 pl-10"
                    placeholder="vous@exemple.com"
                    required
                    data-testid="login-email-input"
                  />
                </div>
              </div>

              <div>
                <Label className="text-sm text-slate-600">Mot de passe</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => handleChange('password', e.target.value)}
                    className="h-10 pl-10"
                    placeholder="••••••••"
                    required
                    data-testid="login-password-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-10 bg-slate-900 hover:bg-slate-800"
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    Se connecter
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>

              <button
                type="button"
                onClick={() => setMode('forgot')}
                className="w-full text-sm text-slate-500 hover:text-slate-700"
                data-testid="forgot-password-link"
              >
                Mot de passe oublie ?
              </button>
            </form>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-slate-400 mt-4">
          Interface commerciale Sellsy / Outlook
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
